/**
 * Play-test harness detection — deliberately dependency-free so it can be
 * imported from `config.ts` without dragging in the audio/asset graph.
 *
 * The harness is the dev-only multi-client play-test wrapper (see
 * `src/playtest/`). It embeds several copies of this client in iframes, each
 * loaded as `index.html?harness=<clientId>`, and drives their GPS/audio from a
 * map UI in the parent window.
 *
 * Hard gate: the harness shell is available ONLY in dev builds. It injects
 * arbitrary GPS fixes, which in a production location game would be a cheat
 * vector — so production bundles never recognise the `?harness=` parameter.
 */

/**
 * The harness client id for this frame, or null when not running under the
 * play-test harness. Requires: a dev build, an iframe (not the top window), and
 * a non-empty `?harness=` query parameter.
 */
export function harnessClientId(): string | null {
  if (!__WILDHUNT_DEV__) return null;
  if (typeof window === 'undefined' || window.parent === window) return null;
  try {
    const id = new URLSearchParams(location.search).get('harness');
    return id || null;
  } catch {
    return null;
  }
}

/**
 * In harness mode the iframe should talk to the server that served it (the
 * play-test host), so the whole setup is self-contained on one origin. Returns
 * a `ws(s)://host/` URL derived from the current origin, or undefined when not
 * in harness mode (callers then keep the build-time `serverUrl`).
 */
export function harnessServerUrl(): string | undefined {
  if (harnessClientId() == null) return undefined;
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${location.host}/`;
}
